import { ref, watch, toValue, type MaybeRefOrGetter } from "vue";
import { useApiClient } from "@/api/client";
import ExportDO = App.Data.ExportDO;

/**
 * Fetches a single export by uuid and keeps it in sync whenever the uuid
 * (or the `enabled` gate) changes. Stale in-flight responses are discarded so
 * rapidly switching exports never shows the wrong one.
 */
export const useExport = (
	uuid: MaybeRefOrGetter<string | undefined>,
	enabled: MaybeRefOrGetter<boolean> = true,
) => {
	const exportItem = ref<ExportDO>();
	const isLoading = ref(false);

	let requestId = 0;

	const fetchExport = async () => {
		const id = toValue(uuid);
		if (!id || !toValue(enabled)) {
			return;
		}

		const current = ++requestId;
		isLoading.value = true;

		const response = await useApiClient("exports").show(id);

		// A newer fetch started while this one was in flight; drop the result.
		if (current !== requestId) {
			return;
		}

		if (response.isOk()) {
			exportItem.value = response.value;
		}
		isLoading.value = false;
	};

	watch([() => toValue(uuid), () => toValue(enabled)], fetchExport, {
		immediate: true,
	});

	return { exportItem, isLoading, refresh: fetchExport };
};